/**
 * Scene three.js cho lát cắt dọc Phố chợ (M2).
 *
 * - Camera phối cảnh góc cao (kiểu HD-2D diorama), lưới theo kích thước
 *   khu vực từng cấp (ô = 1 đơn vị thế giới, tương ứng sprite 48px).
 * - Ánh sáng ngày/đêm theo giờ game: keyframe nội suy.
 * - Hiệu ứng trong ngân sách 20h (master-plan 3.5, 8): chỉ bloom + bóng PCF.
 *   Tilt-shift BỎ (ghi ở decisions-m2.md).
 * - Mặc định render 1/2 độ phân giải (master-plan 3.5).
 * - Không có WebGL2: ném lỗi tiếng Việt thân thiện (không crash).
 *
 * Render chỉ đọc trạng thái core và gọi hàm thuần của core — không chứa luật.
 */
import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { idAssetNoiThat, veCanvasPlaceholder } from '../assets/index.js';
import { kichThuocKhuVuc, oCuaRaVao, timLoaiNoiThat } from '../core/noi-that.js';
import type { DuLieuGame, MonNoiThatDat } from '../core/types.js';

/** Ô lưới (x, y) → tọa độ thế giới (giữa ô), cửa hàng căn giữa gốc tọa độ. */
export function oSangTheGioi(
  x: number,
  y: number,
  rong: number,
  cao: number,
): { x: number; z: number } {
  return { x: x - rong / 2 + 0.5, z: y - cao / 2 + 0.5 };
}

interface KeyframeSang {
  gio: number;
  nen: [number, number, number];
  mauMatTroi: [number, number, number];
  cuongDoMatTroi: number;
  cuongDoBanCau: number;
}

/** Keyframe ngày/đêm (giờ game) — hằng thiết kế M2. */
const KEYFRAME_SANG: KeyframeSang[] = [
  {
    gio: 0,
    nen: [0.05, 0.07, 0.16],
    mauMatTroi: [0.4, 0.5, 0.9],
    cuongDoMatTroi: 0.12,
    cuongDoBanCau: 0.25,
  },
  {
    gio: 5,
    nen: [0.05, 0.07, 0.16],
    mauMatTroi: [0.4, 0.5, 0.9],
    cuongDoMatTroi: 0.12,
    cuongDoBanCau: 0.25,
  },
  {
    gio: 7,
    nen: [0.98, 0.75, 0.55],
    mauMatTroi: [1.0, 0.75, 0.5],
    cuongDoMatTroi: 0.9,
    cuongDoBanCau: 0.5,
  },
  {
    gio: 12,
    nen: [0.53, 0.81, 0.98],
    mauMatTroi: [1.0, 0.98, 0.92],
    cuongDoMatTroi: 1.35,
    cuongDoBanCau: 0.65,
  },
  {
    gio: 17,
    nen: [0.99, 0.72, 0.45],
    mauMatTroi: [1.0, 0.7, 0.42],
    cuongDoMatTroi: 0.85,
    cuongDoBanCau: 0.5,
  },
  {
    gio: 19,
    nen: [0.12, 0.1, 0.24],
    mauMatTroi: [0.5, 0.45, 0.8],
    cuongDoMatTroi: 0.25,
    cuongDoBanCau: 0.3,
  },
  {
    gio: 21,
    nen: [0.05, 0.07, 0.16],
    mauMatTroi: [0.4, 0.5, 0.9],
    cuongDoMatTroi: 0.12,
    cuongDoBanCau: 0.25,
  },
  {
    gio: 24,
    nen: [0.05, 0.07, 0.16],
    mauMatTroi: [0.4, 0.5, 0.9],
    cuongDoMatTroi: 0.12,
    cuongDoBanCau: 0.25,
  },
];

function noiSuyKeyframe(gio: number): KeyframeSang {
  const g = ((gio % 24) + 24) % 24;
  let truoc = KEYFRAME_SANG[0]!;
  let sau = KEYFRAME_SANG[KEYFRAME_SANG.length - 1]!;
  for (let i = 0; i < KEYFRAME_SANG.length - 1; i++) {
    const a = KEYFRAME_SANG[i]!;
    const b = KEYFRAME_SANG[i + 1]!;
    if (g >= a.gio && g <= b.gio) {
      truoc = a;
      sau = b;
      break;
    }
  }
  const khoang = sau.gio - truoc.gio || 1;
  const t = Math.min(1, Math.max(0, (g - truoc.gio) / khoang));
  const tron = (x: number, y: number): number => x + (y - x) * t;
  const tron3 = (
    x: [number, number, number],
    y: [number, number, number],
  ): [number, number, number] => [tron(x[0], y[0]), tron(x[1], y[1]), tron(x[2], y[2])];
  return {
    gio: g,
    nen: tron3(truoc.nen, sau.nen),
    mauMatTroi: tron3(truoc.mauMatTroi, sau.mauMatTroi),
    cuongDoMatTroi: tron(truoc.cuongDoMatTroi, sau.cuongDoMatTroi),
    cuongDoBanCau: tron(truoc.cuongDoBanCau, sau.cuongDoBanCau),
  };
}

export interface GameScene {
  /** Scene three.js — QuanLyKhach thêm sprite khách vào đây. */
  readonly scene: THREE.Scene;
  /** Đổi điểm ảnh trên canvas thành ô lưới (null nếu không trúng sàn). */
  diemAnhSangO(clientX: number, clientY: number): { x: number; y: number } | null;
  /** Vẽ lại sàn + lưới khi đổi cấp cửa hàng / khu vực. */
  veLaiKhuVuc(capCuaHang: number, duLieu: DuLieuGame, maKhu?: string): void;
  /** Hiệu ứng thời tiết M3: 'nang-nong' | 'mua' | 'gio-bien' | null. */
  datThoiTiet(loai: string | null, tatHieuUng: boolean): void;
  /** Nhân vật trong scene M3: nhân viên + NPC (placeholder hình con nhộng). */
  datNhanVat(soThuNgan: number, soXepKe: number, soNpc: number): void;
  /** Vẽ lại nội thất theo bố cục hiện tại. */
  veLaiNoiThat(boCuc: MonNoiThatDat[], capCuaHang: number, duLieu: DuLieuGame): void;
  /** Cập nhật ánh sáng theo giờ trong ngày (0–23). */
  capNhatAnhSang(gioTrongNgay: number): void;
  /** Bật/tắt bloom (cài đặt + giảm chuyển động). */
  datBloom(bat: boolean): void;
  /** Độ phân giải đầy đủ hay 1/2 (mặc định 1/2). */
  datDoPhanGiaiDayDu(dayDu: boolean): void;
  /** Vẽ một khung hình. */
  render(): void;
  resize(): void;
  huy(): void;
}

export function taoGameScene(canvas: HTMLCanvasElement): GameScene {
  let renderer: THREE.WebGLRenderer;
  try {
    if (typeof window === 'undefined' || !('WebGL2RenderingContext' in window)) {
      throw new Error('no-webgl2');
    }
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  } catch {
    throw new Error(
      'Thiết bị hoặc trình duyệt của bạn không hỗ trợ WebGL2 nên game chưa chạy được. ' +
        'Hãy thử Chrome, Edge hoặc Safari bản mới nhất.',
    );
  }
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  // Mặc định 1/2 độ phân giải (master-plan 3.5).
  let heSoDoPhanGiai = 0.5;
  const apDungDoPhanGiai = (): void => {
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2) * heSoDoPhanGiai);
  };
  apDungDoPhanGiai();

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 300);

  const matTroi = new THREE.DirectionalLight(0xffffff, 1.2);
  matTroi.castShadow = true;
  matTroi.shadow.mapSize.set(1024, 1024);
  scene.add(matTroi);
  scene.add(matTroi.target);
  const banCau = new THREE.HemisphereLight(0xbdd8ff, 0x8a6a4a, 0.6);
  scene.add(banCau);

  let nhomKhuVuc: THREE.Group | null = null;
  let nhomNoiThat: THREE.Group | null = null;
  let rongHienTai = 8;
  let caoHienTai = 6;

  const datCamera = (): void => {
    const lonNhat = Math.max(rongHienTai, caoHienTai);
    camera.position.set(0, lonNhat * 1.05, lonNhat * 0.85);
    camera.lookAt(0, 0, 0.5);
    const trai = -6.5;
    const phai = 6.5;
    const doSau = 25;
    matTroi.position.set(8, 14, 6);
    matTroi.target.position.set(0, 0, 0);
    const cam = matTroi.shadow.camera;
    cam.left = trai;
    cam.right = phai;
    cam.top = 8;
    cam.bottom = -8;
    cam.near = 1;
    cam.far = doSau + 20;
    cam.updateProjectionMatrix();
  };

  const textureLap = (id: string, lapX: number, lapY: number): THREE.CanvasTexture => {
    const tex = new THREE.CanvasTexture(veCanvasPlaceholder(id));
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(lapX, lapY);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  };

  /** Giải phóng geometry/material/texture của một nhóm trước khi vẽ lại. */
  const giaiPhongNhom = (nhom: THREE.Group | null): void => {
    if (!nhom) return;
    nhom.traverse((vat) => {
      const luoi = vat as THREE.Mesh;
      if (luoi.isMesh) {
        luoi.geometry.dispose();
        const chatLieu = luoi.material as THREE.Material | THREE.Material[];
        for (const cl of Array.isArray(chatLieu) ? chatLieu : [chatLieu]) {
          const coMap = cl as THREE.MeshLambertMaterial;
          coMap.map?.dispose();
          cl.dispose();
        }
      } else {
        const duong = vat as THREE.LineSegments;
        if (duong.isLineSegments) {
          duong.geometry.dispose();
          (duong.material as THREE.Material).dispose();
        }
      }
    });
  };

  let maKhuHienTai = 'pho-cho';
  const veLaiKhuVuc = (capCuaHang: number, duLieu: DuLieuGame, maKhu?: string): void => {
    if (maKhu) maKhuHienTai = maKhu;
    const kv = kichThuocKhuVuc(capCuaHang, duLieu);
    rongHienTai = kv.rong;
    caoHienTai = kv.cao;
    if (nhomKhuVuc) {
      scene.remove(nhomKhuVuc);
      giaiPhongNhom(nhomKhuVuc);
    }
    nhomKhuVuc = new THREE.Group();

    // Vỉa hè quanh cửa hàng.
    const viaHe = new THREE.Mesh(
      new THREE.PlaneGeometry(rongHienTai + 10, caoHienTai + 10),
      new THREE.MeshLambertMaterial({ map: textureLap('via-he', 18, 18) }),
    );
    viaHe.rotation.x = -Math.PI / 2;
    viaHe.position.y = -0.02;
    viaHe.receiveShadow = true;
    nhomKhuVuc.add(viaHe);

    // Sàn trong cửa hàng.
    const san = new THREE.Mesh(
      new THREE.PlaneGeometry(rongHienTai, caoHienTai),
      new THREE.MeshLambertMaterial({
        map: textureLap(
          maKhuHienTai === 'bai-bien'
            ? 'san-bai-bien'
            : maKhuHienTai === 'cho-dem'
              ? 'san-cho-dem'
              : 'san-pho-cho',
          rongHienTai,
          caoHienTai,
        ),
      }),
    );
    san.rotation.x = -Math.PI / 2;
    san.receiveShadow = true;
    nhomKhuVuc.add(san);

    // Đường lưới mờ 1 ô.
    const diem: number[] = [];
    for (let x = 0; x <= rongHienTai; x++) {
      const wx = x - rongHienTai / 2;
      diem.push(wx, 0.01, -caoHienTai / 2, wx, 0.01, caoHienTai / 2);
    }
    for (let y = 0; y <= caoHienTai; y++) {
      const wz = y - caoHienTai / 2;
      diem.push(-rongHienTai / 2, 0.01, wz, rongHienTai / 2, 0.01, wz);
    }
    const hinh = new THREE.BufferGeometry();
    hinh.setAttribute('position', new THREE.Float32BufferAttribute(diem, 3));
    nhomKhuVuc.add(
      new THREE.LineSegments(
        hinh,
        new THREE.LineBasicMaterial({ color: 0x6a4a2a, transparent: true, opacity: 0.35 }),
      ),
    );

    // Biển hiệu ở mép bắc.
    const bien = new THREE.Mesh(
      new THREE.PlaneGeometry(4, 1),
      new THREE.MeshBasicMaterial({
        map: new THREE.CanvasTexture(veCanvasPlaceholder('bien-hieu')),
        transparent: false,
      }),
    );
    bien.position.set(0, 1.6, -caoHienTai / 2 - 0.4);
    nhomKhuVuc.add(bien);
    const cotBien = new THREE.Mesh(
      new THREE.BoxGeometry(0.15, 1.6, 0.15),
      new THREE.MeshLambertMaterial({ color: 0x5e3a1a }),
    );
    cotBien.position.set(0, 0.8, -caoHienTai / 2 - 0.45);
    nhomKhuVuc.add(cotBien);

    // Đánh dấu ô cửa ngay mép nam ngoài sàn để thấy rõ lối vào.
    const cua = oCuaRaVao(kv);
    const p = oSangTheGioi(cua.x, cua.y, rongHienTai, caoHienTai);
    const oCua = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map: new THREE.CanvasTexture(veCanvasPlaceholder('cua-ra-vao')),
      }),
    );
    oCua.rotation.x = -Math.PI / 2;
    oCua.position.set(p.x, 0.015, caoHienTai / 2 + 0.5);
    nhomKhuVuc.add(oCua);

    scene.add(nhomKhuVuc);
    datCamera();
  };

  const veLaiNoiThat = (boCuc: MonNoiThatDat[], capCuaHang: number, duLieu: DuLieuGame): void => {
    const kv = kichThuocKhuVuc(capCuaHang, duLieu);
    if (nhomNoiThat) {
      scene.remove(nhomNoiThat);
      giaiPhongNhom(nhomNoiThat);
    }
    nhomNoiThat = new THREE.Group();
    for (const mon of boCuc) {
      const loai = timLoaiNoiThat(mon.ma, duLieu);
      const tex = new THREE.CanvasTexture(veCanvasPlaceholder(idAssetNoiThat(mon.ma)));
      tex.colorSpace = THREE.SRGBColorSpace;
      const hop = new THREE.Mesh(
        new THREE.BoxGeometry(loai.rong, 0.9, loai.cao),
        new THREE.MeshLambertMaterial({ map: tex }),
      );
      const p = oSangTheGioi(
        mon.x + loai.rong / 2 - 0.5,
        mon.y + loai.cao / 2 - 0.5,
        kv.rong,
        kv.cao,
      );
      hop.position.set(p.x, 0.45, p.z);
      hop.castShadow = true;
      hop.receiveShadow = true;
      nhomNoiThat.add(hop);
    }
    scene.add(nhomNoiThat);
  };

  const capNhatAnhSang = (gioTrongNgay: number): void => {
    const kf = noiSuyKeyframe(gioTrongNgay);
    scene.background = new THREE.Color(kf.nen[0], kf.nen[1], kf.nen[2]);
    matTroi.color.setRGB(kf.mauMatTroi[0], kf.mauMatTroi[1], kf.mauMatTroi[2]);
    matTroi.intensity = kf.cuongDoMatTroi;
    banCau.intensity = kf.cuongDoBanCau;
  };

  // Bloom (giữ trong ngân sách 20h hiệu ứng — tilt-shift bỏ theo master-plan 8).
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloomPass = new UnrealBloomPass(new THREE.Vector2(800, 600), 0.32, 0.55, 0.85);
  composer.addPass(bloomPass);
  composer.addPass(new OutputPass());
  let dungBloom = true;

  let thoiGianTruoc = performance.now();
  const render = (): void => {
    const bayGio = performance.now();
    const dt = Math.min(0.1, (bayGio - thoiGianTruoc) / 1000);
    thoiGianTruoc = bayGio;
    capNhatThoiTiet(dt);
    if (dungBloom) {
      composer.render();
    } else {
      renderer.render(scene, camera);
    }
  };

  const resize = (): void => {
    const w = canvas.clientWidth || canvas.parentElement?.clientWidth || 800;
    const h = canvas.clientHeight || canvas.parentElement?.clientHeight || 600;
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };

  const tia = new THREE.Raycaster();
  const matPhangSan = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const diemAnhSangO = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const rect = canvas.getBoundingClientRect();
    const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -((clientY - rect.top) / rect.height) * 2 + 1;
    tia.setFromCamera(new THREE.Vector2(nx, ny), camera);
    const giao = new THREE.Vector3();
    if (!tia.ray.intersectPlane(matPhangSan, giao)) return null;
    const x = Math.floor(giao.x + rongHienTai / 2);
    const y = Math.floor(giao.z + caoHienTai / 2);
    if (x < 0 || y < 0 || x >= rongHienTai || y >= caoHienTai) return null;
    return { x, y };
  };

  // --- M3: thời tiết (hạt mưa / vệt gió đơn giản, tắt khi giảm chuyển động) ---
  let nhomThoiTiet: THREE.Group | null = null;
  const datThoiTiet = (loai: string | null, tatHieuUng: boolean): void => {
    if (nhomThoiTiet) {
      scene.remove(nhomThoiTiet);
      giaiPhongNhom(nhomThoiTiet);
      nhomThoiTiet = null;
    }
    if (!loai || tatHieuUng) return;
    nhomThoiTiet = new THREE.Group();
    if (loai === 'mua') {
      // Mưa: 200 hạt rơi (điểm).
      const n = 200;
      const pos = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) {
        pos[i * 3] = (Math.random() - 0.5) * 30;
        pos[i * 3 + 1] = Math.random() * 12;
        pos[i * 3 + 2] = (Math.random() - 0.5) * 30;
      }
      const hinh = new THREE.BufferGeometry();
      hinh.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      const chat = new THREE.PointsMaterial({
        color: 0x9fc8e8,
        size: 0.12,
        transparent: true,
        opacity: 0.7,
      });
      const mua = new THREE.Points(hinh, chat);
      mua.userData.roi = true;
      nhomThoiTiet.add(mua);
    } else if (loai === 'gio-bien') {
      // Gió biển: vệt ngang di chuyển.
      for (let i = 0; i < 24; i++) {
        const vet = new THREE.Mesh(
          new THREE.PlaneGeometry(1.6, 0.06),
          new THREE.MeshBasicMaterial({ color: 0xd8f0ff, transparent: true, opacity: 0.35 }),
        );
        vet.position.set(
          (Math.random() - 0.5) * 24,
          1 + Math.random() * 5,
          (Math.random() - 0.5) * 24,
        );
        vet.userData.gio = 2 + Math.random() * 3;
        nhomThoiTiet.add(vet);
      }
    }
    // nắng nóng: không hạt — tăng sáng xử lý ở capNhatAnhSang.
    scene.add(nhomThoiTiet);
  };

  // --- M3: nhân vật placeholder (nhân viên + NPC) ---
  let nhomNhanVat: THREE.Group | null = null;
  const taoNguoi = (mau: number, x: number, z: number): THREE.Group => {
    const g = new THREE.Group();
    const than = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.22, 0.5, 4, 8),
      new THREE.MeshLambertMaterial({ color: mau }),
    );
    than.position.y = 0.65;
    than.castShadow = true;
    const dau = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 12, 10),
      new THREE.MeshLambertMaterial({ color: 0xf2c89b }),
    );
    dau.position.y = 1.25;
    dau.castShadow = true;
    g.add(than, dau);
    g.position.set(x, 0, z);
    return g;
  };
  const datNhanVat = (soThuNgan: number, soXepKe: number, soNpc: number): void => {
    if (nhomNhanVat) {
      scene.remove(nhomNhanVat);
      giaiPhongNhom(nhomNhanVat);
      nhomNhanVat = null;
    }
    nhomNhanVat = new THREE.Group();
    // Thu ngân đứng sau quầy (phía bắc), xếp kệ đi dọc lối.
    for (let i = 0; i < soThuNgan; i++)
      nhomNhanVat.add(taoNguoi(0x2e7fd0, -1 + i * 0.8, -caoHienTai / 2 + 1.2));
    for (let i = 0; i < soXepKe; i++) nhomNhanVat.add(taoNguoi(0x3fae5a, 1.5 + i * 0.8, 0.5));
    // NPC đứng ngoài vỉa hè.
    for (let i = 0; i < soNpc; i++) {
      nhomNhanVat.add(taoNguoi(0xd0803a, -4 + i * 1.1, caoHienTai / 2 + 2.5));
    }
    scene.add(nhomNhanVat);
  };

  // Cập nhật hạt mưa / vệt gió mỗi khung (gọi từ render).
  const capNhatThoiTiet = (dt: number): void => {
    if (!nhomThoiTiet) return;
    for (const obj of nhomThoiTiet.children) {
      if (obj.userData.roi && obj instanceof THREE.Points) {
        const pos = obj.geometry.getAttribute('position') as THREE.BufferAttribute;
        for (let i = 0; i < pos.count; i++) {
          let y = pos.getY(i) - dt * 9;
          if (y < 0) y = 12;
          pos.setY(i, y);
        }
        pos.needsUpdate = true;
      } else if (obj.userData.gio) {
        obj.position.x += (obj.userData.gio as number) * dt;
        if (obj.position.x > 14) obj.position.x = -14;
      }
    }
  };

  return {
    scene,
    diemAnhSangO,
    datThoiTiet,
    datNhanVat,
    veLaiKhuVuc,
    veLaiNoiThat,
    capNhatAnhSang,
    datBloom: (bat: boolean) => {
      dungBloom = bat;
    },
    datDoPhanGiaiDayDu: (dayDu: boolean) => {
      heSoDoPhanGiai = dayDu ? 1 : 0.5;
      apDungDoPhanGiai();
      resize();
    },
    render,
    resize,
    huy: () => {
      renderer.dispose();
      composer.dispose();
    },
  };
}
